<!-- 3rd Address Field newFlowTest/"Other Billing Address" -->
<!-- eslint-disable max-len -->
<template>
  <main class="u-container">
    <form
      novalidate
      @submit.prevent="submit"
    >
      <section>
        <h1 class="font-bold text-2xl">
          Official USPS<sup>®</sup> Change-of-Address
        </h1>
        <ProgressBar
          :steps="6"
          :current="4"
        />
        <h2
          ref="progressHeading"
          class="mt-6 text-xl"
        >
          Step 4: <span class="font-bold">Verify Identity</span>
        </h2>
        <!-- Banner Messages -->
        <p
          v-if="movingFromPoBox"
          ref="bannerOldPoBox"
          class="mt-3 text-sm"
        >
          <strong>You are changing your address from a Post Office Box.</strong> Our address database has recognized your old address as a post office box.
          <template v-if="moverType === 'BUSINESS'">
            Please note that the USPS policy only allows the boxholder customer or authorized representative of the organization listed on <i>PS Form 1093, Application for Post Office Box Service</i> to file Change-of-Address (COA) orders. Forwarding of mail for other persons receiving mail at the box is the responsibility of the organization.
          </template>
          <template v-else>
            Please note that the USPS policy only allows the box customer who signs <i>PS Form 1093, Application for Post Office Box Service</i> to file Change-of-Address orders. Forwarding of mail for other persons receiving mail at the box is the responsibility of the official boxholder.
          </template>
        </p>
        <p
          v-if="newAddressDpo"
          ref="bannerNewDpo"
          class="mt-3 text-sm"
        >
          <strong>You are changing your address to a Diplomatic Post Office address.</strong> Our address database has recognized your new address as a Diplomatic Post Office address, and we will forward your mail as requested. Please note that the USPS policy does not support forwarding mail out of a Diplomatic Post Office addresses. This means if you change your address again in the future, you must contact your Diplomatic Post Office mail clerk to forward your mail.
        </p>
        <p
          v-if="newAddressCmra"
          ref="bannerNewCmra"
          class="mt-3 text-sm"
        >
          <strong>You are changing your address to a Private Mail Box.</strong> Our address database has recognized your new address as a Private Mail Box held at a Commercial Mail Receiving Agency (CMRA), and we will forward your mail as requested. Please note that the USPS policy does not support forwarding mail out of CMRAs. This means if you change your address again in the future, you must contact your Commercial Mail Receiving Agency to forward your mail.
        </p>
        <p
          v-if="newAddressInUniqueZip"
          ref="bannerNewUniqueZip"
          class="mt-3 text-sm"
        >
          <strong>Our address database has recognized your new address as a unique address</strong>, and we will forward your mail as requested. Please note that the USPS policy does not support forwarding mail from a unique address; if you change your address again in the future, you must contact the mail center for this business or institution to forward your mail.
        </p>
        <p
          v-if="newAddressBusiness && moverType !== 'BUSINESS'"
          ref="bannerResToBiz"
          class="mt-3 text-sm"
        >
          <strong>You are changing your address from a residential address to a business address.</strong> Our address database has recognized your new address as a business or organization (e.g., corporations, universities, hospitals, hotels), and we will forward your mail as requested. Please note that the USPS policy does not allow individual or family movers to forward mail from a business/organization address. This means if you change your address again in the future, the business/organization will be responsible for forwarding your mail.
        </p>
        <p
          ref="subheading"
          class="mt-4"
        >
          For your security, we need to verify your identity using a valid credit or debit card. You will be charged {{ icoaAmountFormatted }}. We accept the following cards:
        </p>
        <div class="flex mt-4">
          <img
            :src="$options.AmericanExpressSVG"
            alt="American Express"
            height="32px"
            width="45px"
          >
          <img
            :src="$options.DiscoverSVG"
            alt="Discover"
            class="ml-1"
            height="32px"
            width="45px"
          >
          <img
            :src="$options.VisaSVG"
            alt="VISA"
            class="ml-1"
            height="32px"
            width="45px"
          >
          <img
            :src="$options.MastercardSVG"
            alt="Mastercard"
            class="ml-1"
            height="32px"
            width="45px"
          >
        </div>
      </section>
      <section class="pb-6">
        <template v-if="businessToMilitary">
          <p class="mt-4">
            <strong>Billing address</strong>
          </p>
          <p class="mt-2">
            To verify your identity, the address on file with your credit card company must match your old address.
          </p>
          <p
            ref="bizToMil"
            class="mt-6"
          >
            {{ oldStreetAddress }}<br> {{ oldCity }}, {{ oldState }} {{ formatZip9(oldZip9) }}
          </p>
          <div class="c-hairline mt-6" />
          <p class="mt-4">
            We'll ask for the rest of your credit or debit card information on the next page.
          </p>
        </template>
        <template v-else>
          <Tooltip class="mt-4">
            <p>
              <strong>Select a billing address:</strong>
            </p>
            <template #tip>
              <p ref="tooltipCopy1">
                To verify your identity and prevent fraudulent Change-of-Address requests, we process a {{ icoaAmountFormatted }} fee to your credit or debit card. Your card's billing address should match either the old or new address you entered earlier during this process.
              </p>
              <p class="mt-3">
                Please note that the Internet Change-of-Address Service uses a high level of security on a secure server.
              </p>
            </template>
            <template slot="accessible">
              Billing details
            </template>
          </Tooltip>
          <div class="mt-4 text-center">
            <InputRadio
              ref="oldBillingType"
              v-model="billingAddressType"
              form-value="OLD"
              name="billing-type"
              class="c-input-radio-button--alt mt-1 uppercase"
              button
              @input="analytics.click.oldBillingType"
            >
              {{ hrOldAddress }}
            </InputRadio>
            <InputRadio
              ref="newBillingType"
              v-model="billingAddressType"
              form-value="NEW"
              name="billing-type"
              button
              class="c-input-radio-button--alt mt-1 uppercase"
              @input="analytics.click.newBillingType"
            >
              {{ hrNewAddress }}
            </InputRadio>
            <template v-if="showThirdAddressOption">
              <p class="mt-2">
                or
              </p>
              <button
                ref="otherAddressBtn"
                type="button"
                class="c-link underline"
                @click="clearThirdBillingAddress();
                        clearHelper();
                        billingAddressType = 'OTHER'; analytics.click.otherBillingType();"
              >
                Enter a Different Address
              </button>
              <p
                v-if="billingAddressType === 'OTHER' && informedDelivery"
                ref="informedDeliveryCopy"
                :class="{ 'mt-6': !billingAddressComplete }"
              >
                For security reasons Informed Delivery is currently not available for customers who are registering in conjunction with a third party address. Please visit
                <a
                  href="https://informeddelivery.usps.com/box/pages/intro/start.action"
                  rel="noopener noreferrer"
                  target="_blank"
                  class="c-link c-link--inline"
                >informeddelivery.usps.com</a> for alternative registration options.
              </p>
            </template>
            <p class="mt-6">
              We'll collect the rest of your payment information on the next step.
            </p>
          </div>
        </template>
      </section>
      <transition
        appear
        name="fade"
      >
        <section
          v-if="flagEMFEnabled && emf"
          ref="emf"
        >
          <ExtensionSummary
            ref="emfSummary"
            :extension-cost="emfProperties.optionCost"
            :extension-period="emfProperties.optionPeriod"
            :icoa-amount="icoaAmount || 105"
            show
            class="mt-4 mb-6"
            @clear-selection="emf = ''"
          />
        </section>
      </transition>
      <div class="c-navbar">
        <router-link
          :to="{ name: 'verify-info-read' }"
          replace
          class="c-btn c-btn--secondary"
          @click.native="analytics.click.back"
        >
          Back
        </router-link>
        <button
          ref="submit"
          class="c-btn"
          @click="analytics.click.next()"
        >
          Next
        </button>
      </div>
      <section v-if="billingAddressType === 'OTHER'">
        <div class="c-hairline" />
        <div>
          <section class="mt-8">
            <h3 class="font-bold">
              Enter a different address
            </h3>
            <InputListBox
              ref="billingStreetAddress"
              v-model="billingStreetAddress"
              label="Street Address"
              placeholder="Street Address"
              name="billing-street-address"
              class="mt-2"
              :has-error="errors.billingStreetAddress.hasError"
              :async-fn="streetSearch({
                cityName: billingCity,
                state: billingState,
                zipcode: billingZipCode,
              })"
              :transform-compare="streetCompare"
              :transform-label="streetLabel"
              :transform-value="streetValue"
              @input="billingPoBoxNumber = ''; billingPrivateMailbox = '';"
              @select="billingCity = $event.cityName;
                       billingState = $event.state;
                       billingZipCode = $event.zip;"
            >
              {{ errors.billingStreetAddress.message }}
            </InputListBox>
            <InputListBox
              ref="billingCity"
              v-model="billingCity"
              label="City"
              placeholder="City"
              name="billing-city"
              class="mt-3"
              :has-error="errors.billingCity.hasError"
              :async-fn="citySearch({
                state: billingState,
                zip: billingZipCode,
              })"
              :transform-compare="zipCompare"
              :transform-label="cityLabel"
              :transform-value="cityValue"
              @select="billingState = $event.state; billingZipCode = $event.zip;"
            >
              {{ errors.billingCity.message }}
            </InputListBox>
            <InputState
              ref="billingState"
              v-model="billingState"
              label="State"
              name="billing-state"
              class="mt-3"
              :has-error="errors.billingState.hasError"
            >
              <template #helper>
                {{ errors.billingState.message }}
              </template>
            </InputState>
            <InputZipCode
              ref="billingZipCode"
              v-model="billingZipCode"
              label="ZIP Code™"
              placeholder="00000"
              name="billing-zip-code"
              class="mt-3"
              :has-error="errors.billingZipCode.hasError"
              :async-fn="zipSearch({
                cityName: billingCity,
                state: billingState,
              })"
              :transform-compare="zipCompare"
              :transform-label="zipLabel"
              :transform-value="zipValue"
              @select="billingCity = $event.cityName; billingState = $event.state;"
            >
              <template slot="helper">
                {{ errors.billingZipCode.message }}
              </template>
            </InputZipCode>
            <InputText
              v-if="billingState === 'PR'"
              v-model="billingUrbanizationName"
              label="Urbanization Name (Puerto Rico only)"
              name="billing-urbanization-name"
              placeholder="URB HIGHLAND GDNS"
              class="mt-3"
              :has-error="errors.billingUrbanizationName.hasError"
            >
              <template slot="helper">
                {{ errors.billingUrbanizationName.message }}
              </template>
            </InputText>
            <InputText
              v-if="showBillingPoBox"
              ref="billingPoBox"
              v-model="billingPoBoxNumber"
              name="billing-po-box-number"
              placeholder="PO Box 123"
              class="mt-3"
              :has-error="errors.billingPoBoxNumber.hasError"
            >
              <Tooltip slot="label">
                <span>Po Box</span>
                <template slot="tip">
                  <p>
                    <span class="font-bold">Note:</span> The address you provided is not considered a proper mailing address within this ZIP Code™. A proper mailing address within this ZIP Code™ requires a PO Box address. Please input your PO Box number in the “PO Box” field and click "Next" to continue.<br><span class="font-bold">A PO Box is required to proceed.</span>
                  </p>
                </template>
                <template slot="accessible">
                  Billing address PO box note
                </template>
              </Tooltip>
              <template #helper>
                {{ errors.billingPoBoxNumber.message }}
              </template>
            </InputText>
            <InputText
              v-if="showBillingPrivateMailbox"
              v-model="billingPrivateMailbox"
              label="Private Mailbox"
              name="billing-private-mailbox"
              placeholder="PMB 123"
              :has-error="errors.billingPrivateMailbox.hasError || !billingPrivateMailbox"
            >
              <template #helper>
                {{ errors.billingPrivateMailbox.message }}
              </template>
            </InputText>
          </section>
          <template v-if="bufferStartDate">
            <div class="c-hairline mt-7" />
            <section class="mt-6">
              <h3 class="font-bold">
                We had to change your forwarding start date
              </h3>
              <p
                ref="bufferMessage"
                class="mt-3 text-sm"
              >
                When using a billing address other than your new or old address to validate your identity, the forwarding date must be no sooner than {{ moverType === 'BUSINESS' ? '30 days' : '7 days' }} from today. We've modified your start date to the earliest date possible, but you can make it even later if you prefer.
              </p>
              <InputDatePicker
                ref="bufferDatepicker"
                v-model="bufferedStartDate"
                :min="minBufferedStartDate"
                :max="maxStartDate"
                :open-date="minBufferedStartDate"
                name="buffered-start-date"
                inline
                class="mt-3"
                :has-error="errors.bufferedStartDate.hasError"
              >
                <template #helper>
                  {{ errors.bufferedStartDate.message }}
                </template>
              </InputDatePicker>
              <div class="text-center">
                <p class="mt-3 font-bold">
                  Mail Forwarding Date
                </p>
                <p>{{ getFormattedDate(bufferedStartDate, 'iiii, MMMM d, yyyy') }}</p>
                <p class="mt-3 text-sm">
                  {{ getFormattedDate(minBufferedStartDate, 'MM/dd/yyyy') }} is the soonest we can start forwarding. If you choose a later start date, your end date will also be pushed back.
                </p>
              </div>
              <p
                ref="holdMailNote"
                class="mt-3 text-sm"
              >
                <span class="font-bold">
                  Note:
                </span>
                If you are moving within {{ moverType === 'BUSINESS' ? '30 days' : '7 days' }}, you can have your mail held at your post office for 3 to 30 days by <a
                  href="https://holdmail.usps.com/holdmail/"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="c-link c-link--inline"
                >putting your mail on hold online</a> or visiting your local post office.
              </p>
            </section>
          </template>
          <div class="c-hairline mt-6" />
          <section class="pb-6">
            <h3 class="font-bold mt-6">
              Are you changing your own address or helping someone else?
            </h3>
            <InputRadio
              v-model="preparerType"
              form-value="MOVER"
              name="change-address-type"
              button
              class="c-input-radio-button--alt mt-4 text-center"
              @input="analytics.otherBillingModal.mover"
            >
              I'm changing my own address
            </InputRadio>
            <InputRadio
              v-model="preparerType"
              form-value="PREPARER"
              name="change-address-type"
              button
              class="c-input-radio-button--alt mt-1 text-center"
              @input="analytics.otherBillingModal.preparer"
            >
              I'm helping someone else
            </InputRadio>
            <template
              v-if="preparerType === 'PREPARER'"
            >
              <p class="mt-6">
                <strong>Please enter your own name and contact information</strong>
                (not the mover's information). We need this information in case we need to reach you with any questions about this Change-of-Address request.
              </p>
              <InputText
                ref="preparerFirstName"
                v-model="preparerFirstName"
                label="First Name"
                name="first-name"
                placeholder="First Name"
                class="mt-5"
                :has-error="errors.preparerFirstName.hasError"
              >
                <template #helper>
                  {{ errors.preparerFirstName.message }}
                </template>
              </InputText>
              <InputText
                v-model="preparerMiddleName"
                label="Middle Name or Initial"
                name="middle-name"
                placeholder="Middle Name or Initial"
                class="mt-5"
              />
              <InputText
                ref="preparerLastName"
                v-model="preparerLastName"
                label="Last Name"
                name="last-name"
                placeholder="Last Name"
                class="mt-5"
                :has-error="errors.preparerLastName.hasError"
              >
                <template #helper>
                  {{ errors.preparerLastName.message }}
                </template>
              </InputText>
              <InputText
                ref="preparerEmail"
                v-model.trim="preparerEmail"
                label="Email Address"
                name="email"
                placeholder="Hello@mail.com"
                class="mt-5"
                :has-error="errors.preparerEmail.hasError"
              >
                <template #helper>
                  {{ errors.preparerEmail.message }}
                </template>
              </InputText>
              <InputPhone
                ref="preparerPhoneNumber"
                v-model="preparerPhoneNumber"
                label="Phone Number"
                placeholder="(000) 000-0000"
                name="phone"
                class="mt-3"
                :has-error="errors.preparerPhoneNumber.hasError"
              >
                <template #helper>
                  {{ errors.preparerPhoneNumber.message }}
                </template>
              </InputPhone>
              <InputDropdown
                ref="preparerPhoneType"
                v-model="preparerPhoneType"
                label="Phone Type"
                name="phone-type"
                class="mt-5"
                :has-error="errors.preparerPhoneType.hasError"
              >
                <option value="MOBILE">
                  Mobile
                </option>
                <option value="HOME">
                  Home
                </option>
                <template #helper>
                  {{ errors.preparerPhoneType.message }}
                </template>
              </InputDropdown>
            </template>
          </section>
        </div>
      </section>
    </form>
  </main>
</template>

<script>
/* eslint-disable vue/no-unused-components */
import {
  InputDatePicker,
  InputDropdown,
  InputListBox,
  InputPhone,
  InputRadio,
  InputState,
  InputText,
  InputZipCode,
  ProgressBar,
  Tooltip,
} from '@mgo/ui/lib/components';
import { mapErrors } from '@mgo/core/src/validations/utils';
import { track } from '../../analytics/events';
import { mobileAnalytics } from './analytics';
import ExtensionSummary from '../../components/ExtensionSummary.vue';
import addressViolations from '../../mixins/addressViolations';
import datePicker from '../../mixins/datePicker';
import identityCheckMixin from './mixin';
import rapidKey from '../../mixins/rapidKey';

// SVG imports
import AmericanExpressSVG from '../../assets/images/_logo-american-express.svg';
import DiscoverSVG from '../../assets/images/_logo-discover.svg';
import MastercardSVG from '../../assets/images/_logo-mastercard.svg';
import VisaSVG from '../../assets/images/_logo-visa.svg';

export default {
  name: 'MobileIdentityCheckNoModalTest',
  components: {
    ExtensionSummary,
    InputDatePicker,
    InputDropdown,
    InputListBox,
    InputPhone,
    InputRadio,
    InputState,
    InputText,
    InputZipCode,
    ProgressBar,
    Tooltip,
  },
  mixins: [
    addressViolations,
    datePicker,
    identityCheckMixin,
    rapidKey,
  ],
  data() {
    return {
      recaptchaToken: 'STATIC', // used to pass validation
    };
  },
  computed: {
    analytics() {
      return mobileAnalytics();
    },
    errors() {
      const fieldsToValidate = [];

      if (this.billingAddressType !== 'OTHER') return [];

      fieldsToValidate.push(...[
        'billingStreetAddress',
        'billingCity',
        'billingState',
        'billingZipCode',
      ]);

      if (this.showBillingPrivateMailbox) {
        fieldsToValidate.push('billingPrivateMailbox');
      }

      if (this.showBillingPoBox) {
        fieldsToValidate.push('billingPoBoxNumber');
      }

      if (this.billingState === 'PR') {
        fieldsToValidate.push('billingUrbanizationName');
      }

      if (this.bufferStartDate) {
        fieldsToValidate.push('bufferedStartDate');
      }

      if (this.preparerType === 'PREPARER') {
        fieldsToValidate.push(...[
          'preparerFirstName',
          'preparerLastName',
          'preparerEmail',
          'preparerPhoneNumber',
          'preparerPhoneType',
        ]);
      }

      return mapErrors(this, fieldsToValidate);
    },
  },
  watch: {
    billingAddressType(value, oldValue) {
      if (oldValue === 'OTHER') {
        this.lazy = true;
      }
    },
  },
  methods: {
    async validate() {
      this.billingAddressError = '';
      this.fieldErrors = [];
      this.lazy = false;
      await this.$nextTick();
      if (!this.formIsValid) return this.scrollToFirstError();
      return this.submit();
    },
    clearThirdBillingAddress() {
      if (this.billingAddressType === 'OTHER') return;
      this.billingAddressError = '';
      this.fieldErrors = [];
      this.billingStreetAddress = '';
      this.billingCity = '';
      this.billingState = '';
      this.billingZipCode = '';
      this.billingUrbanizationName = '';
      this.billingPoBoxNumber = '';
      this.billingPrivateMailbox = '';
    },
    clearHelper() {
      if (this.billingAddressType === 'OTHER') return;
      this.fieldErrors = [];
      this.preparerFirstName = '';
      this.perparerMiddleName = '';
      this.preparerLastName = '';
      this.preparerEmail = '';
      this.preparerPhoneNumber = '';
    },
    async savePreparer() {
      const personResponse = await this.$coa.personSave({
        personType: 'PREPARER',
        firstName: this.preparerFirstName,
        middleName: this.preparerMiddleName,
        lastName: this.preparerLastName,
        email: this.preparerEmail,
        emailConfirm: this.preparerEmail,
        [`${this.preparerPhoneType.toLowerCase()}Phone`]: this.preparerPhoneNumber,
        phoneType: this.preparerPhoneType,
      });

      this.attachFieldErrors(personResponse);

      track(this.preparerUUID ? 'PersonUpdated' : 'PersonCreated',
        {
          personType: 'PREPARER',
          phoneType: this.preparerPhoneType,
        });
    },
    async submit() {
      if (this.$store.state.loading) {
        return;
      }

      this.lazy = false;
      await this.$nextTick();

      if (!this.formIsValid) {
        this.scrollToFirstError();
        return;
      }

      if (!this.informedDeliveryEligible) {
        this.informedDelivery = false;
      }

      this.$store.commit('ENABLE_LOADER');

      const { xhr } = await this.$coa.coaUpdate({
        ...this.coa,
        billingAddressType: this.billingAddressType,
        informedDelivery: this.informedDelivery,
        startDate: this.bufferStartDate ? this.bufferedStartDate : this.startDate,
        multipleAddresses: this.coa.multipleAddresses,
      });

      this.attachFieldErrors({ xhr });

      if (xhr?.exception || this.fieldErrors?.length) {
        this.$store.commit('DISABLE_LOADER');
        await this.$nextTick();
        this.scrollToFirstError();
        return;
      }

      track('CoaUpdated',
        {
          dcoa: this.dcoa,
          forwardType: this.forwardType,
          informedDelivery: this.informedDelivery,
          moverType: this.moverType,
          startDate: this.startDate,
          stopDate: this.stopDate,
        });

      if (this.preparerType === 'PREPARER') {
        await this.savePreparer();
      }

      const billingPayload = {
        addressType: 'BILLING',
      };

      if (this.billingAddressType === 'OTHER') {
        Object.assign(billingPayload, {
          streetAddress: this.billingStreetAddress,
          city: this.billingCity,
          state: this.billingState,
          zipCode: this.billingZipCode,
          urbanizationName: this.billingUrbanizationName,
          privateMailbox: this.billingPrivateMailbox,
          poBoxNumber: this.billingPoBoxNumber,
        });
      } else {
        [
          'streetAddress',
          'city',
          'state',
          'zipCode',
          'urbanizationName',
          'privateMailbox',
          'poBoxNumber',
        ].forEach((field) => {
          billingPayload[field] = this[`${this.billingAddressType.toLowerCase()}Address`][field];
        });
      }

      const billingAddressResponse = await this.$coa.addressSave(billingPayload);

      this.billingAddressError = billingAddressResponse?.xhr?.exception || '';
      this.attachFieldErrors(billingAddressResponse, 'billing');
      await this.$nextTick();

      // If there are logged errors
      // prevent the next step
      if (this.billingAddressError || this.fieldErrors.length) {
        this.$store.commit('DISABLE_LOADER');
        this.scrollToFirstError();
        return;
      }

      if (this.flagEMFEnabled) {
        await this.$coa.extensionSave(this.emf || 0, this.coa?.visitId);
      }

      // log the amsStatusCodes on 200 responses for billing addresses
      if (billingAddressResponse?.addressType
        && this.isAmsMatch(billingAddressResponse?.amsStatusCode)) {
        this.billingStreetAddress = billingAddressResponse?.streetAddress;
        this.billingCity = billingAddressResponse?.city;
        this.billingState = billingAddressResponse?.state;
        this.billingZipCode = billingAddressResponse?.zipCode;
        this.defaultException(billingAddressResponse.amsStatusCode);
      }

      this.next();
    },
  },
  AmericanExpressSVG,
  DiscoverSVG,
  MastercardSVG,
  VisaSVG,
};
</script>
